import { CircleAlert } from 'lucide-react';
import type * as React from 'react';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/**
 * Label, control, and whatever the control still owes the user.
 *
 * It exists so the gap between a label and its field is decided once. That gap
 * was being written by hand at three different values across the forms, which
 * is the sort of inconsistency that reads as carelessness long before anyone
 * works out what is actually wrong.
 *
 * The hint and error ids are derived from the control's own id, so a caller
 * can point `aria-describedby` at them without inventing a second naming
 * scheme. Error replaces hint rather than stacking below it: two lines of
 * small print under one field is a form apologising for itself.
 */
function Field({
  id,
  label,
  hint,
  error,
  optional,
  required,
  className,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  /** Marked on the field rather than marking every other field "required". */
  optional?: string;
  /**
   * The opposite mark, for a field standing ALONE rather than in a run of them.
   *
   * The guest cards do not use it and should not: every one of their ten fields
   * is required, so ten marks say nothing that the one unmarked exception would
   * not have said better. It earns its place on a field in its own block, where
   * there is no run to compare against and the reader has no way to tell
   * whether leaving it empty is allowed.
   */
  required?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div data-slot="field" className={cn('space-y-2', className)}>
      <Label htmlFor={id}>
        {label}
        {optional && (
          <span className="type-caption font-normal text-foreground-faint">{optional}</span>
        )}
        {/* Louder than `optional`, deliberately. "You may skip this" is a
            reassurance and can afford to be faint; "you cannot send without
            this" is an instruction, and a guest who misses it finds out at the
            send button with no idea which field it meant. */}
        {required && (
          <span className="type-caption font-medium text-destructive-text">{required}</span>
        )}
      </Label>

      {children}

      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="type-caption flex items-start gap-1.5 text-destructive-text"
        >
          <CircleAlert className="mt-px size-3.5 shrink-0" />
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="type-caption text-foreground-faint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/**
 * A titled run of related fields.
 *
 * A ten-question form is four short asks when it is grouped and a wall when it
 * is not, so the grouping is a component rather than a convention people
 * remember to follow.
 */
function FieldGroup({
  title,
  description,
  className,
  children,
}: {
  title: string;
  description?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset data-slot="field-group" className={cn('space-y-4', className)}>
      <legend className="type-eyebrow text-foreground-faint">{title}</legend>
      {description && <p className="type-caption -mt-2 text-foreground-muted">{description}</p>}
      {children}
    </fieldset>
  );
}

/**
 * Two fields side by side above `sm`, stacked below.
 *
 * Used instead of writing the grid at each call site, which is how the guest
 * form ended up with single fields sitting in two-column grids and a ragged
 * right edge down the whole page.
 */
function FieldRow({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('grid gap-4 sm:grid-cols-2 sm:gap-5', className)}>{children}</div>;
}

export { Field, FieldGroup, FieldRow };
